import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Newspaper, Plus, Edit, Trash2, Eye, EyeOff, Calendar, Image, Music, Upload, Loader2, Bold, Italic, Link as LinkIcon, Type, Heading1, Heading2, List } from 'lucide-react';
import { AdminLayout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { getAdminBlogPosts, createBlogPost, updateBlogPost, deleteBlogPost, uploadFile, getAdminMe } from '@/lib/api';

// Fonction de rendu du markdown pour la prévisualisation
const renderMarkdown = (content, fontClass = '') => {
  if (!content) return null;
  
  return content.split('\n').map((line, index) => {
    const trimmedLine = line.trim();
    if (!trimmedLine) return <br key={index} />;
    
    // Headings (check trimmed line)
    if (trimmedLine.startsWith('## ')) {
      const text = trimmedLine.substring(3);
      return <h2 key={index} className={`text-xl font-bold text-charcoal mb-3 ${fontClass}`}>{text}</h2>;
    }
    if (trimmedLine.startsWith('# ')) {
      const text = trimmedLine.substring(2);
      return <h1 key={index} className={`text-2xl font-bold text-charcoal mb-4 ${fontClass}`}>{text}</h1>;
    }
    
    // Process inline formatting
    let rendered = trimmedLine;
    
    // Bold: **text**
    rendered = rendered.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    // Italic: *text* (not **text**)
    rendered = rendered.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>');
    // Links: [text](url)
    rendered = rendered.replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-terracotta underline">$1</a>');
    
    // List items
    if (trimmedLine.startsWith('- ')) {
      rendered = '• ' + rendered.substring(2);
      return (
        <p 
          key={index} 
          className={`text-stone leading-relaxed mb-2 pl-4 ${fontClass}`}
          dangerouslySetInnerHTML={{ __html: rendered }}
        />
      );
    }
    
    return (
      <p 
        key={index} 
        className={`text-stone leading-relaxed mb-3 ${fontClass}`}
        dangerouslySetInnerHTML={{ __html: rendered }}
      />
    );
  });
};

// Options de polices
const FONT_OPTIONS = [
  { value: 'default', label: 'Par défaut (Montserrat)', class: 'font-sans' },
  { value: 'serif', label: 'Élégant (Playfair)', class: 'font-display' },
  { value: 'handwriting', label: 'Manuscrit', class: 'font-cursive' },
];

const AdminBlog = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [editDialog, setEditDialog] = useState({ open: false, post: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, post: null });
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    image_url: '',
    audio_url: '',
    font_style: 'default',
    is_published: true
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState({ image: false, audio: false });
  const [previewTab, setPreviewTab] = useState('edit');
  
  const imageInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      await getAdminMe();
      const response = await getAdminBlogPosts();
      setPosts(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      image_url: '',
      audio_url: '',
      font_style: 'default',
      is_published: true
    });
    setPreviewTab('edit');
    setEditDialog({ open: true, post: null });
  };

  const openEditDialog = (post) => {
    setFormData({
      title: post.title,
      content: post.content,
      excerpt: post.excerpt || '',
      image_url: post.image_url || '',
      audio_url: post.audio_url || '',
      font_style: post.font_style || 'default',
      is_published: post.is_published
    });
    setPreviewTab('edit');
    setEditDialog({ open: true, post });
  };

  const handleFileUpload = async (file, type) => {
    if (!file) return;
    
    setUploading({ ...uploading, [type]: true });
    
    try {
      const response = await uploadFile(file);
      const url = response.data.url;
      
      if (type === 'image') {
        setFormData({ ...formData, image_url: url });
      } else {
        setFormData({ ...formData, audio_url: url });
      }
      
      toast.success(`${type === 'image' ? 'Image' : 'Audio'} uploadé avec succès`);
    } catch (error) {
      toast.error(`Erreur lors de l'upload: ${error.response?.data?.detail || error.message}`);
    } finally {
      setUploading({ ...uploading, [type]: false });
    }
  };

  const insertFormatting = (tag) => {
    const textarea = contentRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.content.substring(start, end);
    
    let newText = '';
    let cursorOffset = 0;
    
    switch (tag) {
      case 'bold':
        newText = `**${selectedText || 'texte en gras'}**`;
        cursorOffset = selectedText ? 0 : -2;
        break;
      case 'italic':
        newText = `*${selectedText || 'texte en italique'}*`;
        cursorOffset = selectedText ? 0 : -1;
        break;
      case 'link':
        newText = `[${selectedText || 'texte du lien'}](https://url)`;
        cursorOffset = selectedText ? 0 : -1;
        break;
      case 'h1':
        newText = `# ${selectedText || 'Titre principal'}`;
        break;
      case 'h2':
        newText = `## ${selectedText || 'Sous-titre'}`;
        break;
      case 'list':
        newText = `- ${selectedText || 'Élément de liste'}`;
        break;
      default:
        return;
    }
    
    const content = formData.content.substring(0, start) + newText + formData.content.substring(end);
    setFormData({ ...formData, content });
    
    // Reposition cursor after formatting
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + newText.length + cursorOffset;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const getFontClass = () => {
    const fontOption = FONT_OPTIONS.find(f => f.value === formData.font_style);
    return fontOption ? fontOption.class : 'font-sans';
  };

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Titre et contenu requis');
      return;
    }

    setSubmitting(true);
    try {
      if (editDialog.post) {
        await updateBlogPost(editDialog.post.id, formData);
        toast.success('Article mis à jour');
      } else {
        await createBlogPost(formData);
        toast.success('Article créé');
      }
      setEditDialog({ open: false, post: null });
      loadData();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.post) return;

    try {
      await deleteBlogPost(deleteDialog.post.id);
      toast.success('Article supprimé');
      setDeleteDialog({ open: false, post: null });
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const togglePublish = async (post) => {
    try {
      await updateBlogPost(post.id, { ...post, is_published: !post.is_published });
      toast.success(post.is_published ? 'Article masqué' : 'Article publié');
      loadData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <AdminLayout title="Blog / Actualités">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-terracotta" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Blog / Actualités">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="bg-white rounded-lg border border-gray-200 px-3 py-2">
            <span className="text-xl sm:text-2xl font-bold text-gray-900">{posts.length}</span>
            <span className="text-sm text-gray-500 ml-2">article{posts.length > 1 ? 's' : ''}</span>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 px-3 py-2">
            <span className="text-xl sm:text-2xl font-bold text-green-600">{posts.filter(p => p.is_published).length}</span>
            <span className="text-sm text-gray-500 ml-2">publié{posts.filter(p => p.is_published).length > 1 ? 's' : ''}</span>
          </div>
        </div>
        <Button data-testid="btn-create-post" onClick={openCreateDialog} className="gap-2 bg-terracotta hover:bg-terracotta/90 w-full sm:w-auto">
          <Plus className="w-4 h-4" />
          Nouvel article
        </Button>
      </div>

      {/* Posts List */}
      {posts.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Newspaper className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">Aucun article pour le moment</p>
            <Button onClick={openCreateDialog} className="bg-terracotta hover:bg-terracotta/90">
              <Plus className="w-4 h-4 mr-2" />
              Créer le premier article
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => (
            <Card key={post.id} className={!post.is_published ? 'opacity-60' : ''}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3 sm:gap-4">
                  {post.image_url && (
                    <img
                      src={post.image_url}
                      alt={post.title}
                      className="w-16 h-16 sm:w-24 sm:h-24 object-cover rounded-lg flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4">
                      <div className="min-w-0">
                        <h3 className="font-semibold text-base sm:text-lg text-gray-900 break-words">{post.title}</h3>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-500">{formatDate(post.created_at)}</span>
                          {post.audio_url && (
                            <Badge variant="outline" className="text-blue-600 border-blue-200">
                              <Music className="w-3 h-3 mr-1" /> Audio
                            </Badge>
                          )}
                          <Badge className={post.is_published ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                            {post.is_published ? (
                              <><Eye className="w-3 h-3 mr-1" /> Publié</>
                            ) : (
                              <><EyeOff className="w-3 h-3 mr-1" /> Brouillon</>
                            )}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0 self-end sm:self-start">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => togglePublish(post)}
                          title={post.is_published ? 'Masquer' : 'Publier'}
                          className="h-9 w-9 p-0"
                        >
                          {post.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button
                          data-testid={`btn-edit-${post.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(post)}
                          className="h-9 w-9 p-0"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid={`btn-delete-${post.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteDialog({ open: true, post })}
                          className="h-9 w-9 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-gray-600 text-sm mt-2 line-clamp-2">
                      {post.excerpt || post.content.substring(0, 150)}...
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit/Create Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ ...editDialog, open })}>
        <DialogContent className="max-w-3xl w-[95vw] sm:w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Newspaper className="w-5 h-5 text-terracotta" />
              {editDialog.post ? "Modifier l'article" : 'Nouvel article'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-5">
            {/* Titre */}
            <div>
              <Label className="text-charcoal font-medium">Titre *</Label>
              <Input
                data-testid="input-post-title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Titre de l'article"
                className="mt-1"
              />
            </div>
            
            {/* Résumé */}
            <div>
              <Label className="text-charcoal font-medium">Résumé (optionnel)</Label>
              <Input
                data-testid="input-post-excerpt"
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                placeholder="Court résumé pour la liste"
                className="mt-1"
              />
            </div>
            
            {/* Upload Image */}
            <div>
              <Label className="text-charcoal font-medium">Image</Label>
              <div className="mt-1 flex items-center gap-3">
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFileUpload(e.target.files[0], 'image')}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => imageInputRef.current?.click()}
                  disabled={uploading.image}
                  className="gap-2"
                >
                  {uploading.image ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Image className="w-4 h-4" />
                  )}
                  {uploading.image ? 'Upload...' : 'Choisir une image'}
                </Button>
                {formData.image_url && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setFormData({ ...formData, image_url: '' })}
                    className="text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
              {formData.image_url && (
                <img
                  src={formData.image_url}
                  alt="Aperçu"
                  className="mt-2 w-full h-48 object-cover rounded-lg"
                />
              )}
            </div>
            
            {/* Sélecteur de police */}
            <div>
              <Label className="text-charcoal font-medium">Style de police</Label>
              <Select 
                value={formData.font_style} 
                onValueChange={(value) => setFormData({ ...formData, font_style: value })}
              >
                <SelectTrigger className="mt-1 w-full md:w-64">
                  <Type className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Choisir une police" />
                </SelectTrigger>
                <SelectContent>
                  {FONT_OPTIONS.map((font) => (
                    <SelectItem key={font.value} value={font.value}>
                      <span className={font.class}>{font.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Contenu avec barre d'outils et prévisualisation */}
            <div>
              <Label className="text-charcoal font-medium">Contenu *</Label>
              
              <Tabs value={previewTab} onValueChange={setPreviewTab} className="mt-1">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="edit">Éditer</TabsTrigger>
                  <TabsTrigger value="preview">Prévisualiser</TabsTrigger>
                </TabsList>
                
                <TabsContent value="edit" className="mt-2">
                  <div className="border rounded-lg overflow-hidden">
                    {/* Toolbar */}
                    <div className="bg-gray-50 border-b px-1 sm:px-2 py-1.5 flex items-center gap-0.5 sm:gap-1 flex-wrap">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => insertFormatting('bold')}
                        title="Gras (**texte**)"
                        className="h-10 w-10 sm:h-8 sm:w-auto sm:px-2 p-0"
                      >
                        <Bold className="w-4 h-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => insertFormatting('italic')}
                        title="Italique (*texte*)"
                        className="h-10 w-10 sm:h-8 sm:w-auto sm:px-2 p-0"
                      >
                        <Italic className="w-4 h-4" />
                      </Button>
                      <div className="w-px h-6 bg-gray-300 mx-1" />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => insertFormatting('h1')}
                        title="Titre principal"
                        className="h-10 w-10 sm:h-8 sm:w-auto sm:px-2 p-0"
                      >
                        <Heading1 className="w-4 h-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => insertFormatting('h2')}
                        title="Sous-titre"
                        className="h-10 w-10 sm:h-8 sm:w-auto sm:px-2 p-0"
                      >
                        <Heading2 className="w-4 h-4" />
                      </Button>
                      <div className="w-px h-6 bg-gray-300 mx-1" />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => insertFormatting('list')}
                        title="Liste"
                        className="h-10 w-10 sm:h-8 sm:w-auto sm:px-2 p-0"
                      >
                        <List className="w-4 h-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => insertFormatting('link')}
                        title="Lien"
                        className="h-10 w-10 sm:h-8 sm:w-auto sm:px-2 p-0"
                      >
                        <LinkIcon className="w-4 h-4" />
                      </Button>
                    </div>
                    <Textarea
                      ref={contentRef}
                      data-testid="input-post-content"
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      placeholder="Écrivez votre article ici...

Utilisez la barre d'outils ou :
**texte** pour le gras
*texte* pour l'italique
# Titre principal
## Sous-titre
- Liste
[texte](url) pour les liens"
                      rows={10}
                      className="border-0 rounded-none focus-visible:ring-0 font-mono text-base sm:text-sm min-h-[200px] sm:min-h-[300px]"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Astuce : Sélectionnez du texte puis cliquez sur un bouton pour appliquer le formatage
                  </p>
                </TabsContent>
                
                <TabsContent value="preview" className="mt-2">
                  <div className="border rounded-lg p-6 min-h-[300px] bg-white">
                    {formData.content ? (
                      <div className={getFontClass()}>
                        {renderMarkdown(formData.content, getFontClass())}
                      </div>
                    ) : (
                      <p className="text-gray-400 italic">Aucun contenu à prévisualiser</p>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
            
            {/* Upload Audio */}
            <div>
              <Label className="text-charcoal font-medium">Audio (optionnel)</Label>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <input
                  ref={audioInputRef}
                  type="file"
                  accept="audio/*"
                  className="hidden"
                  onChange={(e) => handleFileUpload(e.target.files[0], 'audio')}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => audioInputRef.current?.click()}
                  disabled={uploading.audio}
                  className="gap-2 flex-1 sm:flex-none min-h-[44px]"
                  data-testid="btn-upload-audio"
                >
                  {uploading.audio ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Music className="w-4 h-4" />
                  )}
                  {uploading.audio ? 'Upload en cours...' : (formData.audio_url ? 'Changer l\'audio' : 'Ajouter un audio')}
                </Button>
                {formData.audio_url && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setFormData({ ...formData, audio_url: '' })}
                    className="text-red-500 min-h-[44px]"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
              {formData.audio_url && (
                <audio controls className="mt-2 w-full">
                  <source src={formData.audio_url} />
                  Votre navigateur ne supporte pas l'audio.
                </audio>
              )}
            </div>
            
            {/* Publier */}
            <div className="flex items-center gap-2 pt-2 border-t">
              <Switch
                id="is_published"
                checked={formData.is_published}
                onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })}
              />
              <Label htmlFor="is_published">Publier immédiatement</Label>
            </div>
          </div>
          
          <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setEditDialog({ open: false, post: null })} className="w-full sm:w-auto min-h-[44px]">
              Annuler
            </Button>
            <Button
              data-testid="btn-submit-post"
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-terracotta hover:bg-terracotta/90 w-full sm:w-auto min-h-[44px]"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                editDialog.post ? 'Mettre à jour' : 'Créer'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Supprimer cet article ?</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600">
            Êtes-vous sûr de vouloir supprimer "<strong>{deleteDialog.post?.title}</strong>" ? Cette action est irréversible.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, post: null })}>
              Annuler
            </Button>
            <Button data-testid="btn-confirm-delete" variant="destructive" onClick={handleDelete}>
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminBlog;
